import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Mail, RefreshCw, Check, Shield } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OTPVerification = ({ email, onVerified, onSkip }) => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const inputRefs = useRef([]);

    useEffect(() => {
        // Focus first input on mount
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    useEffect(() => {
        // Countdown timer for resend
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const handleChange = (index, value) => {
        // Only allow digits
        if (value && !/^\d+$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value.slice(-1); // Take only last character
        setOtp(newOtp);

        // Auto-focus next input
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (pastedData) {
            const newOtp = [...otp];
            for (let i = 0; i < pastedData.length; i++) {
                newOtp[i] = pastedData[i];
            }
            setOtp(newOtp);
            // Focus last filled input or the next empty one
            const focusIndex = Math.min(pastedData.length, 5);
            inputRefs.current[focusIndex]?.focus();
        }
    };

    const handleVerify = async () => {
        const otpString = otp.join('');
        if (otpString.length !== 6) {
            toast.error('Please enter the complete 6-digit code');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API_URL}/api/auth/verify-otp`, {
                email: email,
                otp: otpString
            });
            toast.success('Email verified successfully!');
            onVerified?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid verification code');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        setResending(true);
        try {
            await axios.post(`${API_URL}/api/auth/resend-otp`, { email });
            toast.success('Verification code sent!');
            setCountdown(60); // 60 second cooldown
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to resend code');
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a1628] flex items-center justify-center p-4">
            <div className="max-w-md w-full">
                <div className="bg-[#0d2847] border border-white/5 p-8 text-center">
                    {/* Icon */}
                    <div className="w-16 h-16 bg-primary/20 mx-auto mb-6 flex items-center justify-center">
                        <Shield className="w-8 h-8 text-primary" />
                    </div>

                    {/* Title */}
                    <h1 className="font-heading font-bold text-2xl uppercase tracking-tight text-white mb-2">
                        Verify Your Email
                    </h1>
                    <p className="text-muted-foreground text-sm mb-8">
                        We've sent a 6-digit verification code to<br />
                        <span className="text-primary font-medium">{email}</span>
                    </p>

                    {/* OTP Input */}
                    <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
                        {otp.map((digit, index) => (
                            <Input
                                key={index}
                                ref={el => inputRefs.current[index] = el}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleChange(index, e.target.value)}
                                onKeyDown={(e) => handleKeyDown(index, e)}
                                className="w-12 h-14 text-center text-2xl font-bold bg-[#0a1628] border-white/10 focus:border-primary text-white"
                                data-testid={`otp-input-${index}`}
                            />
                        ))}
                    </div>

                    {/* Verify Button */}
                    <Button
                        onClick={handleVerify}
                        disabled={loading || otp.join('').length !== 6}
                        className="w-full bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider h-12 mb-4"
                        data-testid="verify-otp-btn"
                    >
                        {loading ? (
                            <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Verifying...
                            </>
                        ) : (
                            <>
                                <Check className="w-4 h-4 mr-2" />
                                Verify Email
                            </>
                        )}
                    </Button>

                    {/* Resend */}
                    <div className="text-sm text-muted-foreground">
                        Didn't receive the code?{' '}
                        {countdown > 0 ? (
                            <span className="text-white">Resend in {countdown}s</span>
                        ) : (
                            <button
                                onClick={handleResendOtp}
                                disabled={resending}
                                className="text-primary hover:underline font-medium"
                                data-testid="resend-otp-btn"
                            >
                                {resending ? 'Sending...' : 'Resend Code'}
                            </button>
                        )}
                    </div>

                    {/* Skip Option (optional) */}
                    {onSkip && (
                        <button
                            onClick={onSkip}
                            className="mt-6 text-xs text-muted-foreground hover:text-white underline"
                            data-testid="skip-verification-btn"
                        >
                            Skip for now
                        </button>
                    )}
                </div>

                {/* Help Text */}
                <p className="text-center text-xs text-muted-foreground mt-4">
                    Check your spam folder if you don't see the email
                </p>
            </div>
        </div>
    );
};

export default OTPVerification;
